// core
import { Injectable } from '@angular/core';

// services
import { PartnerBrandService } from '@campfire-nx/api';

//raptor
import { RAPTOR_TRACKING_OBJECT_NAMES } from '../constant/raptor-gtm-constants';

declare global {
  interface Window { dataLayer: any; }
}

@Injectable({
  providedIn: 'root'
})
export class RaptorGtmService {
  style: any;
  partner: any;

  constructor(
    private partnerBrandService: PartnerBrandService
  ) {
    // Create datalayer
    window.dataLayer = window.dataLayer || [];
    // Partner - get product data
    this.partner = this.partnerBrandService.getPartner();
  }

  /**
   * @param event
   * @description Pushes event to the datalayer, ecommerce events resets datalayer, logs events if not production
   */
  private pushEvent(event: any) {
    // Push the event to the data layer
    window.dataLayer.push(event);
    // Clear the previous ecommerce object.
    if ('ecommerce' in event) {
      window.dataLayer.push({ 'ecommerce': null });
    }
  }
/*
  * @param event
  * @param eventCategory
  * @param eventAction
  * @param eventLabel 
  * @param eventValue 
  * @description Build a static event object using tracking object names constants
  */
 public buildStaticEventObject(event:string, eventAction:string, eventCategory:string, eventLabel:string, eventValue:any) {
   const eventObject:{[key: string]: any} = {};
   eventObject[RAPTOR_TRACKING_OBJECT_NAMES.event] = event;
   eventObject[RAPTOR_TRACKING_OBJECT_NAMES.eventAction] = eventAction;
   eventObject[RAPTOR_TRACKING_OBJECT_NAMES.eventCategory] = eventCategory;
   eventObject[RAPTOR_TRACKING_OBJECT_NAMES.eventLabel] = eventLabel;
   eventObject[RAPTOR_TRACKING_OBJECT_NAMES.eventValue] = eventValue;
   
   return eventObject;
 }

 /**
  * @param event
  * @param step?
  * @param extraValues?
  * @description Build a custom event object retaining property names from the raptor flow.
  */
 public buildCustomEventObject(event: any, step?: any, extraValues?: {[key: string]: any}) {
   // clone the full event object provided from the raptor flow
   let eventObject = JSON.parse(JSON.stringify(event));
   // delete properties that are used for configuration purposes
   delete eventObject.valueFor;
   delete eventObject.type;
   if (step && event.valueFor) {
     eventObject[RAPTOR_TRACKING_OBJECT_NAMES.eventValue] = this.getEventValues(step, event);
   }

   // Check for values starting with an *
   // Process them with processEventValue to retrieve data
   // from the step object.
   if (step) {
     for(const [key, value] of Object.entries(eventObject)) {
       if (typeof value === 'string' && value.startsWith('*')) {
         // pass in the value after the *
         eventObject[key] = this.processEventValue(step, value.substring(1));
       }
     }
   }

   if (extraValues) {
     eventObject = {...eventObject, ...extraValues};
   }
   return eventObject;
 }

  /**
   * @param formData
   * @param step
   * @description Construct question event from form data
   */
  public trackQuestionOnSubmit(formData: any, step: any) {
    if (formData && step) {
      formData.sections?.forEach(({ elements }: any) => {
        elements?.forEach(({ settings, key, uuid }: any) => {
          const tracking = settings?.tracking;
          if (Array.isArray(tracking)) {
            tracking.forEach((event: any) => {
              const questionValue = this.processEventValue(step, key);
              if (event.type === 'question') {
                this.trackEvent(this.buildStaticEventObject(
                  'trackQuestion',
                  'submit-step',
                  key,
                  uuid,
                  questionValue
                ));
              } else if (event.type === 'dynamic') {
                this.trackEvent(this.buildCustomEventObject(
                  event, 
                  step,
                  {
                    'questionValue': questionValue
                  }
                ));
              } else if (event.type === 'custom') {
                this.trackEvent(this.buildStaticEventObject(
                  event.event,
                  event.action,
                  event.category ?? key,
                  event.label ?? uuid,
                  questionValue
                ));
              }
            });
          }
        });
      });
    } else {
      return;
    }
  }

  /**
   * @param question
   * @param questionValue
   * @description Construct custom question events on change
   */
  public trackQuestionOnChange(question: any, questionValue: any) {
    if (question) {
      const tracking = question?.settings?.tracking;
      if (Array.isArray(tracking)) {
        tracking.forEach((event: any) => {
          if (event.type === 'dynamic') {
            this.trackEvent(this.buildCustomEventObject(
              event, 
              false,
              {
                'questionValue': questionValue
              }
            ));
          } else if (event.type === 'custom') {
            this.trackEvent(this.buildStaticEventObject(
              event.event,
              event.action,
              event.category ?? question.key,
              event.label ?? question.uuid,
              questionValue
            ));
          }
        });
      }
    } else {
      return;
    }
  }

  /**
   * @param event
   * @param eventName
   * @param eventCategory
   * @param eventLabel
   * @param eventValue
   * @description Generic tracking event
   */
  public trackEvent(eventObject:object) {
    this.pushEvent(eventObject);
  }

  /**
   * @param formData
   * @param partner
   * @description E-Commerce event - view item (quote)
   */
  public trackEventEcommerceViewItem(formData: any, partner: any, event: any) {
    const quoteValue = this.getQuoteValue(formData);
    const data = {
      event: 'view_item',
      ecommerce: {
        currency: partner?.product?.country?.currency,
        value: quoteValue,
        items: [
          {
            item_id: formData.flow_slug,
            item_name: partner?.product?.label,
            item_brand: partner?.name,
            item_variant: formData.data.cover_level ?? formData.data.cover_type,
            coupon: '',
            price: quoteValue,
            quantity: 1
          }
        ]
      },
    };
    this.pushEvent(data);

    // Alias event
    if (event.alias) {
      this.trackEvent(this.buildStaticEventObject(
        event.alias.event,
        event.alias.action,
        event.alias.category ?? formData.analytics.flow_slug,
        event.alias.label ?? formData.analytics.page_uuid,
        { quote_price: this.getQuoteValue(formData) }
      ));
    }
  }

  /**
   * @param formData
   * @param partner
   * @description E-Commerce event - select item (excess)
   */
  public trackEventEcommerceSelectItem(formData: any, partner: any, event: any) {
    const quoteValue = this.getQuoteValue(formData);
    const data = {
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_id: formData.flow_slug,
            item_name: partner?.product?.label,
            item_brand: partner?.name,
            item_variant: formData.data.cover_level,
            coupon: '',
            price: quoteValue,
            quantity: 1
          }
        ]
      },
    };
    this.pushEvent(data);

    // Alias event
    if (event.alias) {
      this.trackEvent(this.buildStaticEventObject(
        event.alias.event,
        event.alias.action,
        event.alias.category ?? formData.analytics.flow_slug,
        event.alias.label ?? formData.analytics.page_uuid,
        this.getEventValues(formData, event.alias)
      ));
    }
  }

  /**
   * @param formData
   * @param partner
   * @description E-Commerce event - add to cart (excess submit)
   */
  public trackEventEcommerceAddToCart(formData: any, partner: any, event: any) {
    const quoteValue = this.getQuoteValue(formData);
    const data = {
      event: 'add_to_cart',
      ecommerce: {
        currency: partner?.product?.country?.currency,
        value: quoteValue,
        items: [
          {
            item_id: formData.flow_slug,
            item_name: partner?.product?.label,
            item_brand: partner?.name,
            item_variant: formData.data.cover_level,
            coupon: '',
            price: quoteValue,
            quantity: 1
          }
        ]
      },
    };
    this.pushEvent(data);

    // Alias event
    if (event.alias) {
      this.trackEvent(this.buildStaticEventObject(
        event.alias.event,
        event.alias.action,
        event.alias.category ?? formData.analytics.flow_slug,
        event.alias.label ?? formData.analytics.page_uuid,
        { quote_price: this.getQuoteValue(formData) }
      ));
    }
  }

  /**
     * @param formData
     * @param partner
     * @description E-Commerce event - begin checkout (payment page)
     */
  public trackEventEcommerceCheckout(formData: any, partner: any, event: any) {
    const quoteValue = this.getQuoteValue(formData);
    const data = {
      event: 'begin_checkout',
      ecommerce: {
        currency: partner?.product?.country?.currency,
        value: quoteValue,
        coupon: '',
        items: [
          {
            item_id: formData.flow_slug,
            item_name: partner?.product?.label,
            item_brand: partner?.name,
            item_variant: formData.data.cover_level,
            coupon: '',
            price: quoteValue,
            quantity: 1
          }
        ]
      }
    };
    this.pushEvent(data);

    // Alias event
    if (event.alias) {
      this.trackEvent(this.buildStaticEventObject(
        event.alias.event,
        event.alias.action,
        event.alias.category ?? formData.analytics.flow_slug,
        event.alias.label ?? formData.analytics.page_uuid,
        { quote_price: this.getQuoteValue(formData) }
      ));
    }
  }

  /**
   * @param step
   * @param paymentType
   * @param value
   * @description E-Commerce event - add payment info (payment page)
   */
  public trackEventEcommerceAddPaymentInfo(formData: any, step: any, paymentType: string, partner: any) {
    const quoteValue = this.getQuoteValue(formData);
    const data = {
      event: 'add_payment_info',
      ecommerce: {
        currency: partner?.product?.country?.currency,
        value: quoteValue,
        coupon: '',
        payment_type: paymentType,
        items: [
          {
            item_id: step.flow_slug,
            item_name: partner?.product?.label,
            item_brand: partner?.name,
            item_variant: formData.data.cover_level,
            coupon: '',
            price: quoteValue,
            quantity: 1
          }
        ]
      }
    };
    this.pushEvent(data);
  }

  /**
   * @param step
   * @param partner
   * @description E-Commerce event - purchase
   */
  public trackEventEcommercePurchase(formData: any, step: any, partner: any) {
    const quoteValue = this.getQuoteValue(formData);
    const quoteTaxValue = this.calculateTax(formData);
    const data = {
      event: 'purchase',
      ecommerce: {
        transaction_id: step.data.policy_number,
        value: quoteValue,
        tax: quoteTaxValue,
        shipping: 0,
        currency: partner?.product?.country?.currency, 
        coupon: '',
        items: [
          {
            item_id: step.flow_slug,
            item_name: partner?.product?.label,
            item_brand: partner?.name,
            item_variant: formData.data.cover_level,
            coupon: '',
            price: quoteValue,
            quantity: 1
          }
        ]
      }
    };
    this.pushEvent(data);
  }

  /**
   * @param step
   * @param event
   * @returns Object of event supplied values
   * @description Gets values from form data to pass to event
   */
  public getEventValues(step: any, event: any) {
    let valueFor = event.valueFor;
    if (Array.isArray(valueFor)) {
      let reduction = valueFor.reduce((acc: Record<string, any>, key: string) => {
        acc[key] = this.processEventValue(step, key);
        return acc;
      }, {});
      return reduction;
    }
    return { [valueFor]: this.processEventValue(step, valueFor) };
  }

  /**
   * @param step
   * @param formData
   * @returns Value from form data
   * @description Retrieves a specific value from form data
   */
  public processEventValue(step: any, key: any) {
    return step.data && step.data[key];
  }

  /**
   * @param formData
   * @returns Initial quote value as a number
   * @description Works out the pre-selected cover value
   */
  private getInitalQuoteValue(formData: any): number {
    let quoteValue = 0;
    if (formData.covers.length > 0) {
      const frequency = formData.data['frequency'];
      const initialCover = formData.covers.find((coverItem: any) => {
        return coverItem.cover.cover_type === formData.data['cover_type']
      })
      if (initialCover?.premium && initialCover.premium[frequency]) {
        quoteValue = initialCover.premium[frequency].payable_monthly
      }
    }
    
    return quoteValue;
  }

  /**
   * @param formData
   * @returns Quote value as a number
   * @description Gets the quote/policy value, if no selected cover revert to inital quote value
   */
  private getQuoteValue(formData: any): number {
    // this was using the data service but there was a circular dependency
    // I may sort this out in the future
    const selectedCover = this.getSelectedCover(formData);
    if (!selectedCover) {
      return this.getInitalQuoteValue(formData);
    }
    const frequency = formData.data['frequency'];
    const premium = selectedCover.premium[frequency];
    const amount = frequency === 'M' ? premium.payable_monthly : premium.payable;
    return amount || 0;
  }

  /**
   * @param formData
   * @returns Number - tax value
   * @description Calculates total premium tax
   */
  private calculateTax(formData: any) {
    let totalTax = 0;
    // this was using the data service but there was a circular dependency
    // I may sort this out in the future
    const selectedCover = this.getSelectedCover(formData);

    if (selectedCover) {
      const frequency = formData.data['frequency'];
      const taxes = selectedCover.premium[frequency].premium_details.taxes;
      totalTax = taxes.reduce((sum: number, item: { value: string; }) => sum + parseFloat(item.value), 0);
    }
    return totalTax;
  }

  private getSelectedCover(formData: any) {
    if (
      formData.data &&
      formData.data.product_slug &&
      formData.data.cover_type &&
      formData.data.cover_level
    ) {
      return formData.covers.find((coverItem: any) => {
        return coverItem.cover.product_slug === formData.data.product_slug &&
            coverItem.cover.cover_type === formData.data.cover_type &&
            coverItem.cover.cover_level === formData.data.cover_level
      });
    } else {
      return false;
    }
  }
}